import {
  createSignal,
  createEffect,
  createResource,
  onMount,
  batch,
  onCleanup,
} from "solid-js";
import {
  CorrectComponentType,
  customShadowlessElement,
  toHyphenated,
} from "@brytdesigns/web-component-core/utils";
import { correctElementType } from "@brytdesigns/web-component-core";

const cachedRecommendations: Record<string, any> = {};

function createRecommendationsFetcher() {
  let controller: AbortController | null = null;
  return async function (url: string) {
    if (cachedRecommendations[url]) {
      return {
        success: true,
        data: cachedRecommendations[url],
      };
    }

    if (controller) controller.abort();
    controller = new AbortController();

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      controller = null;
    }
  };
}

type Props = {
  dataUrl: string;
  dataSectionId: string;
  dataProductId: string;
  dataIntent: string;
  dataTestId: string;
  dataRecommendationsPerformed: boolean;
};

function createEndpoint(props: Props) {
  return `${props.dataUrl}&product_id=${props.dataProductId}&section_id=${props.dataSectionId}&intent=${props.dataIntent}`;
}

const Component: CorrectComponentType<Props> = (props, { element }) => {
  if (!props.dataUrl)
    return console.warn(
      "product-recommendations: No data-url provided",
      element,
    );
  if (!props.dataSectionId)
    return console.warn(
      "product-recommendations: No data-section-id provided",
      element,
    );
  if (!props.dataProductId)
    return console.warn(
      "product-recommendations: No data-product-id provided",
      element,
    );
  if (!props.dataIntent)
    return console.warn(
      "product-recommendations: No data-intent provided",
      element,
    );

  const [endpoint, setEndpoint] = createSignal<string | null>(null);
  const [hasBeenObservered, setHasBeenObservered] = createSignal(false);

  const fetchRecommendations = createRecommendationsFetcher();

  const [data] = createResource(endpoint, async (url) =>
    fetchRecommendations(url).then((result) => {
      if (!result.success) {
        if (!window.Shopify.designMode) {
          throw new Error(`Server returned ${result.status}`);
        }
      }
      const html = document.createElement("div");
      html.innerHTML = result.data || "";
      const recommendations = html.querySelector(
        `product-recommendations[id="${element.id}"]`,
      );

      if (
        recommendations?.innerHTML &&
        recommendations.innerHTML.trim().length
      ) {
        element.setAttribute(
          toHyphenated("dataRecommendationsPerformed"),
          "true",
        );
        return recommendations.innerHTML;
      } else {
        throw new Error("No recommendations available");
      }
    }),
  );

  const intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      batch(() => {
        setHasBeenObservered(true);
        setEndpoint(createEndpoint(props));
      });
    },
    { rootMargin: "0px 0px 400px 0px" },
  );

  createEffect(() => {
    if (!hasBeenObservered()) return;
    const endpoint = createEndpoint(props);
    setEndpoint(endpoint);
  });

  createEffect(() => {
    if (data.error) {
      element.classList.add("hidden");
      return;
    }

    const html = data();

    if (html) {
      element.classList.remove("hidden");
      element.innerHTML = html;
    }
  });

  onMount(() => {
    intersectionObserver.observe(element);
  });

  onCleanup(() => {
    intersectionObserver.disconnect();
  });
};

customShadowlessElement(
  "product-recommendations",
  {
    dataUrl: "",
    dataSectionId: "",
    dataProductId: "",
    dataIntent: "",
    dataTestId: "",
    dataRecommendationsPerformed: false,
  },
  correctElementType(Component),
);
